import { Text } from '@rbilabs/universal-components';
import { addMonths, format, isAfter } from 'date-fns';

import { routes } from 'navigation/routing';
import { localeStringToDateFnsLocale } from 'pages/support-v2/support-v2-orders-list-page/utils';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { Region } from 'utils/environment/types';

import {
  ExpiringIcon,
  ExpiringPoints,
  ExpiringPointsContainer,
  IconContainer,
  PointsToExpireText,
} from './loyalty-rewards.styled';

function isOverThreeMonths(date: Date): boolean {
  // Get the current date and set the time to midnight to avoid DST issues
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  // Calculate the date 3 months from the current date
  const threeMonthsFromNow = addMonths(currentDate, 3);

  // Return true if the given date is after three months from now
  return isAfter(date, threeMonthsFromNow);
}

export const LoyaltyRewardsExpiringPoints: React.FC = () => {
  const { loyaltyUser } = useLoyaltyUser();

  const { region, locale } = useLocale();

  let expirationDate;
  let currentPoints;

  if (region === Region.US) {
    expirationDate = loyaltyUser?.expirationBuckets?.[0]?.expirationDate || null;
    currentPoints = loyaltyUser?.expirationBuckets?.[0]?.pointsToExpire || 0;
  } else {
    expirationDate = loyaltyUser?.pointsExpiryDateKey || null;
    currentPoints = loyaltyUser?.points || 0;
  }

  // Avoid showing component on some conditions
  if (loyaltyUser && currentPoints <= 0) {
    return null;
  }

  if (!expirationDate) {
    return null;
  }

  const date = new Date(expirationDate);

  // If the date is over three months from now, don't show the component
  const over = isOverThreeMonths(date);
  if (over) {
    return null;
  }

  const dateFormat = 'MMMM do';
  const utc = new Date(date.toUTCString().substring(0, 25));
  const formattedDate = format(utc, dateFormat, { locale: localeStringToDateFnsLocale(locale) });
  if (loyaltyUser && (currentPoints <= 0 || expirationDate === null)) {
    return null;
  }

  return (
    loyaltyUser && (
      <ExpiringPointsContainer>
        <ExpiringPoints to={routes.rewardsHistory}>
          <IconContainer ml="$2">
            <ExpiringIcon />
          </IconContainer>
          <Text pr="$2">
            {`${t('youHave')} `}
            <PointsToExpireText>
              {currentPoints} {`${t('points')} `}
            </PointsToExpireText>
            {t('expiringWithDate', {
              expiringDate: formattedDate,
            })}
          </Text>
        </ExpiringPoints>
      </ExpiringPointsContainer>
    )
  );
};
